import { ErrorMessage, Field, Formik } from 'formik';
import { CheckboxWithLabel } from 'formik-material-ui';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import * as Yup from 'yup';

import { useTheme } from '@material-ui/core';
import { createNewPassword, setLoginLoading } from 'actions/app.actions';
import { BasicTextWithUIGuidelines } from 'component-library/atoms/BasicTextWithUIGuidelines';
import { LinkWithUIGuidelines } from 'component-library/atoms/LinkWithUIGuidelines';
import { CGULabel, InputFieldWithLabel, PrimaryButton } from 'components';
import { ICustomTheme } from 'interfaces';
import { appConfigSelector, loginLoadingSelector } from 'reducers/app.reducer';
import { getPasswordInputSchema } from 'schema';
import redirectAfterAuth from 'utils/redirect-after-auth';
import { ROUTES } from '../../constants';
import * as S from './styles';

type formValuesType = {
  password: string;
  confirmPassword: string;
  isCGUAccepted: boolean;
};

const initialValues: formValuesType = {
  password: '',
  confirmPassword: '',
  isCGUAccepted: false,
};

interface ICreateNewPasswordForm {
  token: string;
}

export default function CreateNewPasswordForm({
  token,
}: ICreateNewPasswordForm) {
  const { t } = useTranslation();
  const theme: ICustomTheme = useTheme();
  const dispatch = useDispatch();
  const isLoading = useSelector(loginLoadingSelector);
  const { externalHomePageUrl, appLocale } = useSelector(appConfigSelector);

  const passwordValidator = getPasswordInputSchema(t);

  const formSchema = Yup.object().shape({
    password: passwordValidator,
    confirmPassword: passwordValidator.oneOf(
      [Yup.ref('password')],
      t('core.password.mismatch'),
    ),
    isCGUAccepted: Yup.boolean().oneOf(
      [true],
      t('auth.signup.required_terms_and_conditions'),
    ),
  });

  const handleSubmit = useCallback(
    ({ password, isCGUAccepted }: formValuesType) => {
      dispatch(
        createNewPassword.request(
          { token, password, isCGUAccepted },
          () => {
            // Redirect to external homepage url
            if (externalHomePageUrl) {
              window.location.href = `${externalHomePageUrl}?redirect_to=/${
                appLocale !== 'en' ? appLocale : ''
              }`;
            } else {
              dispatch(setLoginLoading(false));
              redirectAfterAuth();
            }
          },
        ),
      );
    },
    [dispatch, token, externalHomePageUrl, appLocale],
  );

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={formSchema}
      onSubmit={handleSubmit}
    >
      <S.Form>
        <InputFieldWithLabel
          placeholder={t('auth.global.password_placeholder')}
          label={t('auth.createNewPassword.createMyPassword')}
          name="password"
          textCase="sentence"
          type="password"
        />
        <InputFieldWithLabel
          placeholder={t('auth.global.confirm_password_placeholder')}
          label={t('auth.createNewPassword.confirmMyPassword')}
          name="confirmPassword"
          textCase="sentence"
          type="password"
        />
        <S.CheckboxFormControl
          control={
            <Field
              component={CheckboxWithLabel}
              type="checkbox"
              name="isCGUAccepted"
            />
          }
          label={<CGULabel />}
        />
        <ErrorMessage
          name="isCGUAccepted"
          component={(props) => <S.Error {...props} variant="caption" />}
        />
        <S.SubmitButtonContainer>
          <PrimaryButton
            label={t('auth.reset_password.submit')}
            isDisabled={isLoading}
            isLoading={isLoading}
          />
        </S.SubmitButtonContainer>
        <S.ConnectMessageContainer>
          <BasicTextWithUIGuidelines
            text={t('auth.global.iAlreadyHaveAnAccount')}
          />{' '}
          <LinkWithUIGuidelines
            color={theme.colors.paletteFromBO.main}
            fontWeight="bold"
            url={ROUTES.LOGIN}
            textLink={t('auth.global.login')}
            isInternalLink={true}
            withUnderline
          />
        </S.ConnectMessageContainer>
      </S.Form>
    </Formik>
  );
}
